import CostTierForm, { CostTierUpdate } from 'components/shared/highCostMeds/CostTierForm';
import StandardModal from 'components/shared/StandardModal';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { HighCostMedicationTier } from 'models/HighCostMedicationList';
import { updateHighCostMedicationTiers } from 'services/api/highCostMedicationTiers';
import { useToastActions } from 'stores/toastStore';

export type EditCostTiersModalProps = {
  tiers: HighCostMedicationTier[];
  onClose: () => void;
};

// The customer half of Edit Cost Tiers. The form itself is shared with the admin slideout, so all
// that lives here is the shell it opens in, which endpoint a save goes to, and what it invalidates
// afterwards. See design 003, "Edit Cost Tiers".
export default function EditCostTiersModal({ tiers, onClose }: EditCostTiersModalProps) {
  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const { mutate, isPending } = useMutateWithToast({
    mutationFn: (updates: CostTierUpdate[]) => updateHighCostMedicationTiers({ tiers: updates }),
  });

  const handleSave = (updates: CostTierUpdate[]) =>
    mutate(updates, {
      onSuccess: () => {
        // Both queries: the key feeds the Cost Tier column's tooltip, and that column sorts on
        // position, so a reorder re-sorts the medications behind this modal.
        invalidateQueries({ highCostMedicationTiers: true, highCostMedicationListItems: true });
        addToast({ text: 'Cost tiers successfully updated' });
        onClose();
      },
    });

  return (
    <StandardModal title='Edit Cost Tiers Key' onCancel={onClose}>
      {/* The modal header already names the form, so the shared component drops its own heading and
          keeps the explainer copy in its place. */}
      <CostTierForm tiers={tiers} isSaving={isPending} onSave={handleSave} onCancel={onClose} showTitle={false} />
    </StandardModal>
  );
}
